import React, { useCallback, useEffect, useRef, useState } from "react";
import SecretExpiryNiftyHistory from "./SecretExpiryNiftyHistory";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const REFRESH_SEC = 5;

const NIFTY_INDEX = {
  id: 13,
  seg: "IDX_I",
  label: "NIFTY",
  range: 500,
};

const C = {
  bg: "#04090f",
  panel: "#080f1a",
  card: "#0a1628",
  border: "#f7c94855",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  gold: "#ffd84a",
  text: "#cbd5e1",
  muted: "#8a6a16",
  dim: "#3f3414",
};

const S = {
  card: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
  },
  label: {
    fontSize: 9,
    color: C.muted,
    letterSpacing: 3,
    textTransform: "uppercase",
    marginBottom: 5,
  },
  th: {
    padding: "10px 14px",
    textAlign: "right",
    color: C.gold,
    fontSize: 9,
    letterSpacing: 2,
    fontWeight: "bold",
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: "9px 14px",
    textAlign: "right",
    fontSize: 12,
    borderBottom: "1px solid #151000",
  },
};

const initState = (extra = {}) => ({
  spotPrice: "23400",
  running: false,
  loading: false,
  data: null,
  error: "",
  countdown: REFRESH_SEC,
  lastUpdated: null,
  fetchingSpot: false,
  view: "calls",
  ...extra,
});

const fmtVolCr = value => value != null ? (Number(value || 0) / 1e7).toFixed(2) : "-";
const fmtOI = value => value != null ? Number(value || 0).toLocaleString("en-IN") : "0";
const fmtLTP = value => Number(value || 0) ? Number(value).toFixed(2) : "-";
const fmtTime = value => value ? new Date(value).toLocaleTimeString("en-IN", { hour12: false }) : "";

function readJson(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || "") || fallback;
  } catch {
    return fallback;
  }
}

function roundNiftySpotTo50(value) {
  const spot = Number(value || 0);
  if (!spot) return "";
  return String(Math.round(spot / 50) * 50);
}

async function safeFetch(url, body, retries = 2) {
  for (let i = 0; i <= retries; i += 1) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        body: JSON.stringify(body || {}),
      });

      const data = await res.json().catch(() => ({}));

      if (data.error && i < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      return data;
    } catch (err) {
      if (i === retries) throw err;
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  return {};
}

async function fetchLiveSpot() {
  try {
    const data = await safeFetch(`${API_BASE}/api/secret-expiry/options/live-spot`, {
      underlyingId: NIFTY_INDEX.id,
      segment: NIFTY_INDEX.seg,
    }, 1);

    return data.spot || null;
  } catch {
    return null;
  }
}

function Controls({
  label,
  spotPrice,
  setSpotPrice,
  running,
  loading,
  error,
  countdown,
  lastUpdated,
  onStart,
  onStop,
  onFetchSpot,
  fetchingSpot,
  refreshSec,
  accentColor = C.gold,
}) {
  return (
    <div
      style={{
        background: C.panel,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: "14px 16px",
        marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div style={S.label}>{label} SPOT PRICE</div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number"
              value={spotPrice}
              onChange={event => setSpotPrice(event.target.value)}
              disabled={running}
              style={{
                background: "#020202",
                border: `1px solid ${C.border}`,
                color: accentColor,
                padding: "8px 12px",
                borderRadius: 4,
                fontSize: 15,
                fontFamily: "inherit",
                width: 120,
                fontWeight: "bold",
              }}
            />

            <button
              type="button"
              onClick={onFetchSpot}
              disabled={running || fetchingSpot}
              style={{
                background: "#050505",
                border: `1px solid ${C.border}`,
                color: accentColor,
                padding: "8px 10px",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 10,
                fontFamily: "inherit",
                fontWeight: "bold",
              }}
            >
              {fetchingSpot ? "LOADING" : "LIVE"}
            </button>
          </div>
        </div>

        {!running ? (
          <button
            type="button"
            onClick={onStart}
            disabled={loading}
            style={{
              background: "#006b49",
              border: "1px solid #00b37a",
              color: "#fff",
              padding: "9px 22px",
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: 2,
              fontWeight: "bold",
              fontSize: 11,
              boxShadow: "0 0 12px rgba(0,179,122,0.35)",
            }}
          >
            START
          </button>
        ) : (
          <button
            type="button"
            onClick={onStop}
            style={{
              background: "#7f0013",
              border: "1px solid #ef4444",
              color: "#fff",
              padding: "9px 22px",
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: 2,
              fontWeight: "bold",
              fontSize: 11,
              boxShadow: "0 0 12px rgba(239,68,68,0.35)",
            }}
          >
            STOP
          </button>
        )}

        {running && (
          <div style={{ minWidth: 70 }}>
            <div style={S.label}>REFRESH IN</div>
            <div style={{ fontSize: 22, color: accentColor, fontWeight: "bold", lineHeight: 1 }}>
              {countdown}s
            </div>
          </div>
        )}

        <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {loading && <span style={{ fontSize: 11, color: C.amber, letterSpacing: 2 }}>FETCHING DATA...</span>}
          {error && <span style={{ fontSize: 11, color: C.red }}>{error}</span>}
          {lastUpdated && !loading && <span style={{ fontSize: 10, color: C.muted }}>UPDATED {fmtTime(lastUpdated)}</span>}
          {running && !loading && !error && (
            <span style={{ fontSize: 10, color: C.green }}>
              LIVE - AUTO REFRESH {refreshSec || REFRESH_SEC}s
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SentimentBar({ ceVal, peVal, ceLabel, peLabel, title }) {
  const total = Number(ceVal || 0) + Number(peVal || 0);
  if (!total) return null;

  const cePct = ((Number(ceVal || 0) / total) * 100).toFixed(1);

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 4 }}>
        <span style={{ color: C.green }}>{ceLabel}</span>
        <span style={{ color: C.muted, fontSize: 8 }}>{title}</span>
        <span style={{ color: C.red }}>{peLabel}</span>
      </div>

      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${cePct}%`, background: C.green, transition: "width 0.8s ease" }} />
        <div style={{ flex: 1, background: C.red }} />
      </div>
    </div>
  );
}

function NiftyITMPage({ state, setState, refreshSec }) {
  const liveRefreshSec = Math.max(1, Number(refreshSec || REFRESH_SEC));
  const { spotPrice, running, loading, data, error, countdown, lastUpdated, fetchingSpot } = state;
  const ivRef = useRef(null);
  const cdRef = useRef(null);

  const set = useCallback((key, value) => {
    setState(prev => ({ ...prev, [key]: value }));
  }, [setState]);

  const fetchChain = useCallback(async () => {
    set("loading", true);

    try {
      const liveSpot = await fetchLiveSpot();
      const nextSpotPrice = liveSpot
        ? roundNiftySpotTo50(liveSpot)
        : spotPrice;
      if (liveSpot) set("spotPrice", nextSpotPrice);

      const expData = await safeFetch(`${API_BASE}/api/secret-expiry/options/expiry`, {
        underlyingId: NIFTY_INDEX.id,
        segment: NIFTY_INDEX.seg,
      });

      if (!expData?.expiry) throw new Error("No expiry found");

      const chainData = await safeFetch(`${API_BASE}/api/secret-expiry/options/chain`, {
        expiry: expData.expiry,
        spotPrice: Number(nextSpotPrice || 0),
        underlyingId: NIFTY_INDEX.id,
        segment: NIFTY_INDEX.seg,
        range: NIFTY_INDEX.range,
      });

      if (chainData?.error) throw new Error(chainData.error);

      set("data", chainData);
      set("error", "");
      set("lastUpdated", new Date().toISOString());
      set("countdown", liveRefreshSec);
    } catch (err) {
      set("error", err.message || "NIFTY chain fetch failed");
    } finally {
      set("loading", false);
    }
  }, [spotPrice, liveRefreshSec, set]);

  const start = useCallback(async () => {
    set("running", true);
    set("error", "");

    await fetchChain();

    clearInterval(ivRef.current);
    clearInterval(cdRef.current);

    ivRef.current = setInterval(fetchChain, liveRefreshSec * 1000);
    cdRef.current = setInterval(() => {
      setState(prev => ({
        ...prev,
        countdown: prev.countdown <= 1 ? liveRefreshSec : prev.countdown - 1,
      }));
    }, 1000);
  }, [fetchChain, liveRefreshSec, set, setState]);

  const stop = useCallback(() => {
    clearInterval(ivRef.current);
    clearInterval(cdRef.current);
    ivRef.current = null;
    cdRef.current = null;
    set("running", false);
    set("countdown", liveRefreshSec);
  }, [liveRefreshSec, set]);

  useEffect(() => {
    if (running && !ivRef.current) {
      start();
    }

    return () => {
      clearInterval(ivRef.current);
      clearInterval(cdRef.current);
    };
  }, [running, start]);

  const onFetchSpot = async () => {
    set("fetchingSpot", true);
    const spot = await fetchLiveSpot();
    if (spot) set("spotPrice", roundNiftySpotTo50(spot));
    set("fetchingSpot", false);
  };

  const getSentiment = summary => {
    if (!summary) return null;

    const volumeBias = summary.ceVolumeCr > summary.peVolumeCr ? 1 : summary.ceVolumeCr < summary.peVolumeCr ? -1 : 0;
    const oiBias = summary.ceOI > summary.peOI ? 1 : summary.ceOI < summary.peOI ? -1 : 0;
    const totalBias = volumeBias + oiBias;

    if (totalBias >= 1) {
      return { label: "CALL BUYERS AND PUT SELLERS DOMINANT - BULLISH", color: C.green };
    }

    if (totalBias <= -1) {
      return { label: "PUT BUYERS AND CALL SELLERS DOMINANT - BEARISH", color: C.red };
    }

    return { label: "BALANCED - NO CLEAR DOMINANCE", color: C.amber };
  };

  const sentiment = data ? getSentiment(data.summary) : null;

  return (
    <div>
      <Controls
        label="NIFTY"
        spotPrice={spotPrice}
        setSpotPrice={value => set("spotPrice", value)}
        running={running}
        loading={loading}
        error={error}
        countdown={countdown}
        lastUpdated={lastUpdated}
        fetchingSpot={fetchingSpot}
        onStart={start}
        onStop={stop}
        onFetchSpot={onFetchSpot}
        refreshSec={liveRefreshSec}
        accentColor={C.gold}
      />

      {sentiment && (
        <div style={{ ...S.card, borderColor: `${sentiment.color}80`, padding: "16px 20px", marginBottom: 16 }}>
          <div style={S.label}>MARKET SENTIMENT - NIFTY ITM ANALYSIS</div>
          <div style={{ fontSize: 14, color: sentiment.color, fontWeight: "bold", letterSpacing: 2, marginBottom: 16 }}>
            {sentiment.label}
          </div>

          <SentimentBar
            ceVal={data.summary.ceVolumeCr}
            peVal={data.summary.peVolumeCr}
            ceLabel={`CE VOL ${data.summary.ceVolumeCr}`}
            peLabel={`PE VOL ${data.summary.peVolumeCr}`}
            title="VOLUME"
          />

          <SentimentBar
            ceVal={data.summary.ceOI}
            peVal={data.summary.peOI}
            ceLabel={`CE OI ${fmtOI(data.summary.ceOI)}`}
            peLabel={`PE OI ${fmtOI(data.summary.peOI)}`}
            title="OPEN INTEREST"
          />
        </div>
      )}

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 10, marginBottom: 16 }}>
          {[
            { label: "CE VOLUME", value: data.summary.ceVolumeCr, color: C.green },
            { label: "PE VOLUME", value: data.summary.peVolumeCr, color: C.red },
            { label: "CE OPEN INTEREST", value: fmtOI(data.summary.ceOI), color: C.green },
            { label: "PE OPEN INTEREST", value: fmtOI(data.summary.peOI), color: C.red },
          ].map(item => (
            <div key={item.label} style={{ ...S.card, padding: "14px 16px" }}>
              <div style={S.label}>{item.label}</div>
              <div style={{ fontSize: 18, color: item.color, fontWeight: "bold", marginTop: 4 }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(320px, 1fr))", gap: 16 }}>
          {[
            { title: "TOP 10 ITM CALLS - NIFTY CE", rows: data.calls || [], color: C.green },
            { title: "TOP 10 ITM PUTS - NIFTY PE", rows: data.puts || [], color: C.red },
          ].map(table => (
            <div key={table.title} style={{ ...S.card, overflow: "hidden" }}>
              <div
                style={{
                  background: "#020202",
                  padding: "10px 16px",
                  fontSize: 10,
                  color: table.color,
                  letterSpacing: 3,
                  fontWeight: "bold",
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                {table.title}
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["STRIKE", "LTP", "VOLUME", "OI"].map(head => (
                      <th key={head} style={S.th}>{head}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {table.rows.map((row, index) => (
                    <tr key={`${table.title}-${row.strikePrice}-${index}`} style={{ background: index % 2 === 0 ? "#050505" : C.card }}>
                      <td style={{ ...S.td, color: table.color, fontWeight: "bold" }}>{row.strikePrice}</td>
                      <td style={{ ...S.td, color: C.gold }}>{fmtLTP(row.ltp)}</td>
                      <td style={{ ...S.td, color: C.text }}>{fmtVolCr(row.volume)}</td>
                      <td style={{ ...S.td, color: C.text }}>{fmtOI(row.oi)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {!data && !loading && (
        <div style={{ textAlign: "center", padding: "100px 0", color: C.dim }}>
          <div style={{ fontSize: 11, letterSpacing: 4, color: C.muted }}>
            PRESS START TO BEGIN NIFTY ITM SCAN
          </div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: C.dim, marginTop: 8 }}>
            NSE/BSE DATA ONLY - AUTO REFRESH EVERY {liveRefreshSec} SECONDS
          </div>
        </div>
      )}
    </div>
  );
}

function OIAnalysisPage({ state, setState, refreshSec }) {
  const liveRefreshSec = Math.max(1, Number(refreshSec || REFRESH_SEC));
  const { spotPrice, running, loading, data, error, countdown, view, lastUpdated, fetchingSpot } = state;
  const ivRef = useRef(null);
  const cdRef = useRef(null);

  const set = useCallback((key, value) => {
    setState(prev => ({ ...prev, [key]: value }));
  }, [setState]);

  const fetchOI = useCallback(async () => {
    set("loading", true);

    try {
      const liveSpot = await fetchLiveSpot();
      const nextSpotPrice = liveSpot
        ? roundNiftySpotTo50(liveSpot)
        : spotPrice;
      if (liveSpot) set("spotPrice", nextSpotPrice);

      const expData = await safeFetch(`${API_BASE}/api/secret-expiry/options/expiry`, {
        underlyingId: NIFTY_INDEX.id,
        segment: NIFTY_INDEX.seg,
      });

      if (!expData?.expiry) throw new Error("No expiry found");

      const oiData = await safeFetch(`${API_BASE}/api/secret-expiry/options/oi-analysis`, {
        expiry: expData.expiry,
        spotPrice: Number(nextSpotPrice || 0),
        underlyingId: NIFTY_INDEX.id,
        segment: NIFTY_INDEX.seg,
      });

      if (oiData?.error) throw new Error(oiData.error);

      set("data", oiData);
      set("error", "");
      set("lastUpdated", new Date().toISOString());
      set("countdown", liveRefreshSec);
    } catch (err) {
      set("error", err.message || "NIFTY OI fetch failed");
    } finally {
      set("loading", false);
    }
  }, [spotPrice, liveRefreshSec, set]);

  const start = useCallback(async () => {
    set("running", true);
    set("error", "");

    await fetchOI();

    clearInterval(ivRef.current);
    clearInterval(cdRef.current);

    ivRef.current = setInterval(fetchOI, liveRefreshSec * 1000);
    cdRef.current = setInterval(() => {
      setState(prev => ({
        ...prev,
        countdown: prev.countdown <= 1 ? liveRefreshSec : prev.countdown - 1,
      }));
    }, 1000);
  }, [fetchOI, liveRefreshSec, set, setState]);

  const stop = useCallback(() => {
    clearInterval(ivRef.current);
    clearInterval(cdRef.current);
    ivRef.current = null;
    cdRef.current = null;
    set("running", false);
    set("countdown", liveRefreshSec);
  }, [liveRefreshSec, set]);

  useEffect(() => {
    if (running && !ivRef.current) {
      start();
    }

    return () => {
      clearInterval(ivRef.current);
      clearInterval(cdRef.current);
    };
  }, [running, start]);

  const onFetchSpot = async () => {
    set("fetchingSpot", true);
    const spot = await fetchLiveSpot();
    if (spot) set("spotPrice", roundNiftySpotTo50(spot));
    set("fetchingSpot", false);
  };

  const rows = data
  ? view === "calls"
    ? data.calls || []
    : data.puts || []
  : [];

  const pcrColor = !data
    ? C.muted
    : data.pcr < 0.7
      ? C.red
      : data.pcr > 1.5
        ? C.green
        : C.amber;

  const ceOIChange = data ? (data.calls || []).reduce((sum, row) => sum + Number(row.oiChange || 0), 0) : 0;
  const peOIChange = data ? (data.puts || []).reduce((sum, row) => sum + Number(row.oiChange || 0), 0) : 0;

  const supports = data ? [...(data.puts || [])].sort((a, b) => (b.oi || 0) - (a.oi || 0)).slice(0, 4) : [];
  const resistances = data ? [...(data.calls || [])].sort((a, b) => (b.oi || 0) - (a.oi || 0)).slice(0, 4) : [];

  const labelColor = label => {
    if (label === "ATM") return C.amber;
    if (label === "ITM") return view === "calls" ? C.green : C.red;
    if (label === "OTM") return view === "calls" ? C.red : C.green;
    return C.muted;
  };

  return (
    <div>
      <style>{`
        @keyframes sellerZoneBlink {
          0%, 100% {
            opacity: 1;
            box-shadow: none;
          }
          50% {
            opacity: 0.35;
            box-shadow: 0 0 10px currentColor;
          }
        }

        .seller-zone-change {
          display: inline-block;
          margin-top: 4px;
          padding: 2px 7px;
          border: 1px solid currentColor;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
          animation: sellerZoneBlink 0.7s ease-in-out infinite;
        }
      `}</style>
      <Controls
        label="NIFTY"
        spotPrice={spotPrice}
        setSpotPrice={value => set("spotPrice", value)}
        running={running}
        loading={loading}
        error={error}
        countdown={countdown}
        lastUpdated={lastUpdated}
        fetchingSpot={fetchingSpot}
        onStart={start}
        onStop={stop}
        onFetchSpot={onFetchSpot}
        refreshSec={liveRefreshSec}
        accentColor={C.amber}
      />

      {data && (
        <div style={{ ...S.card, borderColor: `${pcrColor}80`, padding: "20px", marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 24, alignItems: "center" }}>
            <div>
              <div style={S.label}>PCR RATIO</div>
              <div style={{ fontSize: 48, color: pcrColor, fontWeight: "bold", lineHeight: 1 }}>
                {data.pcr}
              </div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 4, letterSpacing: 1 }}>
                PE OI / CE OI
              </div>
            </div>

            <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24 }}>
              <div style={S.label}>SIGNAL</div>
              <div style={{ fontSize: 18, color: pcrColor, fontWeight: "bold", letterSpacing: 2, marginBottom: 6 }}>
                {data.pcrSignal}
              </div>
              <div style={{ fontSize: 11, color: C.text, lineHeight: 1.6 }}>{data.pcrDesc}</div>
            </div>

            <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24 }}>
              <div>
                <div style={S.label}>CALL SELLERS</div>
                <div style={{ fontSize: 18, color: C.red, fontWeight: "bold" }}>{fmtOI(data.totalCeOI)}</div>
              </div>
              <div style={{ marginTop: 10 }}>
                <div style={S.label}>PUT SELLERS</div>
                <div style={{ fontSize: 18, color: C.green, fontWeight: "bold" }}>{fmtOI(data.totalPeOI)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
          <div style={{ ...S.card, padding: "14px 16px" }}>
            <div style={{ ...S.label, color: C.red }}>TOP 4 RESISTANCE CE OI</div>
            {resistances.map((row, index) => {
              const change = Number(row.oiChange || 0);
              const added = change > 0;
              const left = change < 0;

              return (
                <div
                  key={`res-${row.strike}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: "1px solid #151000",
                  }}
                >
                  <span style={{ color: C.red, fontWeight: "bold" }}>
                    {row.strike}
                  </span>

                  <div style={{ textAlign: "right" }}>
                    <div>{fmtOI(row.oi)}</div>

                    <span
                      key={`res-change-${row.strike}-${change}`}
                      className={change !== 0 ? "seller-zone-change" : ""}
                      style={{
                        color: added ? C.red : left ? C.green : C.muted,
                      }}
                    >
                      {added
                        ? `[▲ +${fmtOI(change)} ADDED]`
                        : left
                          ? `[▼ ${fmtOI(change)} LEFT]`
                          : "[• 0 CHANGE]"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ ...S.card, padding: "14px 16px" }}>
            <div style={{ ...S.label, color: C.green }}>TOP 4 SUPPORT PE OI</div>
            {supports.map((row, index) => {
              const change = Number(row.oiChange || 0);
              const added = change > 0;
              const left = change < 0;

              return (
                <div
                  key={`sup-${row.strike}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: "1px solid #151000",
                  }}
                >
                  <span style={{ color: C.green, fontWeight: "bold" }}>
                    {row.strike}
                  </span>

                  <div style={{ textAlign: "right" }}>
                    <div>{fmtOI(row.oi)}</div>

                    <span
                      key={`sup-change-${row.strike}-${change}`}
                      className={change !== 0 ? "seller-zone-change" : ""}
                      style={{
                        color: added ? C.green : left ? C.red : C.muted,
                      }}
                    >
                      {added
                        ? `[▲ +${fmtOI(change)} ADDED]`
                        : left
                          ? `[▼ ${fmtOI(change)} LEFT]`
                          : "[• 0 CHANGE]"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data && (
        <div style={{ ...S.card, borderColor: C.border, padding: "16px 20px", marginBottom: 14 }}>
          <div style={S.label}>OI CHANGE DIRECTION</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ background: "#020202", borderRadius: 6, padding: "12px 16px" }}>
              <div style={S.label}>CE OI CHANGE</div>
              <div style={{ fontSize: 20, color: ceOIChange >= 0 ? C.green : C.red, fontWeight: "bold" }}>
                {ceOIChange >= 0 ? "+" : ""}{fmtOI(ceOIChange)}
              </div>
            </div>

            <div style={{ background: "#020202", borderRadius: 6, padding: "12px 16px" }}>
              <div style={S.label}>PE OI CHANGE</div>
              <div style={{ fontSize: 20, color: peOIChange >= 0 ? C.green : C.red, fontWeight: "bold" }}>
                {peOIChange >= 0 ? "+" : ""}{fmtOI(peOIChange)}
              </div>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div style={{ display: "flex", marginBottom: 14, borderRadius: 6, overflow: "hidden", border: `1px solid ${C.border}`, width: "fit-content" }}>
          {[
            { id: "calls", label: "CALL SELLERS CE", color: C.red },
            { id: "puts", label: "PUT SELLERS PE", color: C.green },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => set("view", tab.id)}
              style={{
                padding: "8px 18px",
                fontSize: 11,
                letterSpacing: 2,
                fontFamily: "inherit",
                cursor: "pointer",
                border: "none",
                background: view === tab.id ? "#020202" : C.card,
                color: view === tab.id ? tab.color : C.muted,
                borderBottom: view === tab.id ? `2px solid ${tab.color}` : "2px solid transparent",
                fontWeight: view === tab.id ? "bold" : "normal",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {data && (
        <div style={{ ...S.card, overflow: "hidden" }}>
          <div
            style={{
              background: "#020202",
              padding: "10px 16px",
              fontSize: 10,
              color: view === "calls" ? C.red : C.green,
              letterSpacing: 3,
              fontWeight: "bold",
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            {view === "calls" ? "CALL SELLERS - CE OI" : "PUT SELLERS - PE OI"}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["TYPE", "STRIKE", "LTP", "OI", "OI CHANGE", "VOLUME"].map(head => (
                  <th key={head} style={S.th}>{head}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => (
                <tr key={`${view}-${row.strike}-${index}`} style={{ background: row.label === "ATM" ? "#151000" : index % 2 === 0 ? "#050505" : C.card }}>
                  <td style={{ ...S.td, textAlign: "left", paddingLeft: 16 }}>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: "bold",
                        letterSpacing: 1,
                        color: labelColor(row.label),
                        border: `1px solid ${labelColor(row.label)}80`,
                        padding: "2px 7px",
                        borderRadius: 3,
                      }}
                    >
                      {row.label}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: row.label === "ATM" ? C.amber : C.text, fontWeight: row.label === "ATM" ? "bold" : "normal" }}>
                    {row.strike}
                  </td>
                  <td style={{ ...S.td, color: C.gold }}>{fmtLTP(row.ltp)}</td>
                  <td style={{ ...S.td, color: view === "calls" ? C.red : C.green, fontWeight: "bold" }}>{fmtOI(row.oi)}</td>
                  <td style={{ ...S.td, color: Number(row.oiChange || 0) >= 0 ? C.green : C.red }}>
                    {Number(row.oiChange || 0) >= 0 ? "+" : ""}{fmtOI(row.oiChange || 0)}
                  </td>
                  <td style={{ ...S.td, color: C.text }}>{fmtVolCr(row.volume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!data && !loading && (
        <div style={{ textAlign: "center", padding: "100px 0", color: C.dim }}>
          <div style={{ fontSize: 11, letterSpacing: 4, color: C.muted }}>
            PRESS START TO BEGIN NIFTY OI ANALYSIS
          </div>
        </div>
      )}
    </div>
  );
}

export default function SecretExpiryNifty({ refreshSec = REFRESH_SEC }) {
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem("secret_expiry_nifty_active_view_v1") || "nifty-itm";
  });

  const [itmState, setItmState] = useState(() => {
    return readJson("secret_expiry_nifty_itm_state_v1", initState({ spotPrice: "23400" }));
  });

  const [oiState, setOiState] = useState(() => {
    return readJson("secret_expiry_nifty_oi_state_v1", initState({ spotPrice: "23400", view: "calls" }));
  });

  useEffect(() => {
    localStorage.setItem("secret_expiry_nifty_active_view_v1", activeView);
  }, [activeView]);

  useEffect(() => {
    localStorage.setItem("secret_expiry_nifty_itm_state_v1", JSON.stringify({
      ...itmState,
      loading: false,
      fetchingSpot: false,
    }));
  }, [itmState]);

  useEffect(() => {
    localStorage.setItem("secret_expiry_nifty_oi_state_v1", JSON.stringify({
      ...oiState,
      loading: false,
      fetchingSpot: false,
    }));
  }, [oiState]);

  const tabStyle = tabId => {
    const active = activeView === tabId;

    return {
      background: active ? "#006b49" : "#020202",
      border: `1px solid ${active ? "#00b37a" : C.border}`,
      color: active ? "#ffffff" : C.gold,
      padding: "10px 22px",
      borderRadius: 4,
      cursor: "pointer",
      fontFamily: "inherit",
      letterSpacing: 3,
      fontSize: 11,
      fontWeight: "bold",
      boxShadow: active ? "0 0 16px rgba(0,179,122,0.35)" : "0 0 12px rgba(255,216,74,0.10)",
    };
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setActiveView("nifty-itm")}
          style={tabStyle("nifty-itm")}
        >
          NIFTY ITM
        </button>

        <button
          type="button"
          onClick={() => setActiveView("oi-analysis")}
          style={tabStyle("oi-analysis")}
        >
          OI ANALYSIS
        </button>

        <button
          type="button"
          onClick={() => setActiveView("nifty-history")}
          style={tabStyle("nifty-history")}
        >
          5 MIN HISTORY
        </button>
      </div>

      <div style={{ display: activeView === "nifty-itm" ? "block" : "none" }}>
        <NiftyITMPage state={itmState} setState={setItmState} refreshSec={refreshSec} />
      </div>

      <div style={{ display: activeView === "oi-analysis" ? "block" : "none" }}>
        <OIAnalysisPage state={oiState} setState={setOiState} refreshSec={refreshSec} />
      </div>

      {activeView === "nifty-history" && (
      <div>
        <SecretExpiryNiftyHistory />
      </div>
      )}
    </div>
  );
}
